import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCategorias, getSubcategorias, getPublicaciones } from '../services/publicacionesService';
import { useInstitucion } from '../context/InstitucionContext';
import HeroHeader from '../components/HeroHeader';
import Footer from '../components/Footer';

const MAXIMO_ACCESOS = 3;

const HomePage = () => {
  const [categories, setCategories] = useState([]);
  // Accesos directos por tarjeta: slug de la categoría → primeros hijos (máx. MAXIMO_ACCESOS)
  const [accesos, setAccesos] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { institucion, paleta: colors, ruta } = useInstitucion();
  const institucionSlug = institucion.slug;

  useEffect(() => {
    getCategorias(institucionSlug)
      .then(setCategories)
      .catch(() => setError('No se pudieron cargar las categorías'))
      .finally(() => setLoading(false));
  }, [institucionSlug]);

  // Vista previa de los hijos de cada categoría, igual que en CategoryPage: por la regla
  // hoja/contenedor sólo una de las dos listas trae datos, así que se piden ambas en paralelo
  // y gana la que venga con contenido (evita un /detalle previo por tarjeta).
  useEffect(() => {
    if (categories.length === 0) {
      setAccesos({});
      return undefined;
    }
    let vigente = true;
    Promise.all(
      categories.map(async (cat) => {
        const [hijas, publicacionesHijas] = await Promise.all([
          getSubcategorias(cat.slug).catch(() => []),
          getPublicaciones(cat.slug).catch(() => []),
        ]);
        const items = hijas.length > 0
          ? hijas.map((h) => ({
            clave: `c-${h.id}`,
            nombre: h.nombre,
            destino: `/${h.slug}`,
            esCategoria: true,
          }))
          : publicacionesHijas.map((p) => ({
            clave: `p-${p.id}`,
            // Misma jerarquía que la grilla de publicaciones: la descripción es el nombre visible
            nombre: p.descripcion || p.titulo,
            destino: `/${cat.slug}/${p.slug}`,
            esCategoria: false,
          }));
        return { slug: cat.slug, items };
      })
    ).then((resultados) => {
      if (!vigente) return;
      const mapa = {};
      resultados.forEach(({ slug, items }) => { mapa[slug] = items; });
      setAccesos(mapa);
    });
    return () => { vigente = false; };
  }, [categories]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <HeroHeader titulo="Centro de Publicaciones" subtitulo={institucion.descripcion} />

      {/* Main Content */}
      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="mb-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Categorías de Publicaciones
          </h2>
          <p className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto">
            Selecciona una categoría para explorar las publicaciones disponibles
          </p>
        </div>

        {/* Categories Grid */}
        {loading ? (
          <div className="text-center py-16 text-gray-400">Cargando categorías...</div>
        ) : error ? (
          <div className="text-center py-16 text-red-500">{error}</div>
        ) : categories.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center border border-gray-200">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: `${colors.primary}15` }}>
              <svg className="h-8 w-8 flex-shrink-0" fill="none" stroke={colors.primary} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No hay categorías disponibles</h3>
            <p className="text-gray-500">Crea categorías desde el panel de administración para comenzar</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {categories.map((category) => {
              const items = accesos[category.slug] || [];
              const visibles = items.slice(0, MAXIMO_ACCESOS);
              return (
                // La tarjeta ya no es un <Link> envolvente: los accesos directos son enlaces
                // propios y anidar <a> dentro de <a> es HTML inválido. El `group` se queda en
                // el contenedor para que el hover siga animando toda la tarjeta.
                <div
                  key={category.id}
                  className="group relative flex flex-col bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-gray-300 hover:-translate-y-1"
                >
                  <Link to={ruta(`/${category.slug}`)} className="block">
                    {/* Card Header Gradient */}
                    <div
                      className="relative h-36 overflow-hidden"
                      style={{
                        background: `linear-gradient(135deg, ${colors.cardGradientFrom}, ${colors.cardGradientVia}, ${colors.cardGradientTo})`
                      }}
                    >
                      {/* Decorative circles */}
                      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10" />
                      <div className="absolute -bottom-4 -left-4 w-20 h-20 rounded-full bg-white/10" />

                      {/* Icon */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="flex items-center justify-center w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl group-hover:scale-110 transition-transform duration-300">
                          <svg className="w-7 h-7 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="px-5 pt-5 pb-3">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {category.nombre}
                      </h3>
                      <p className="text-sm text-gray-500 line-clamp-2 leading-relaxed">
                        {category.descripcion}
                      </p>
                    </div>
                  </Link>

                  {/* Accesos directos a los primeros hijos. `mt-auto` en el pie mantiene las
                      tarjetas de una misma fila alineadas aunque tengan distinta cantidad. */}
                  {visibles.length > 0 && (
                    <ul className="px-3 pb-1 space-y-0.5">
                      {visibles.map((item) => (
                        <li key={item.clave}>
                          <Link
                            to={ruta(item.destino)}
                            className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                            title={item.nombre}
                          >
                            <svg
                              className="w-4 h-4 flex-shrink-0 opacity-60"
                              style={{ color: colors.primary }}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            >
                              {item.esCategoria ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              )}
                            </svg>
                            <span className="truncate">{item.nombre}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* CTA */}
                  <Link
                    to={ruta(`/${category.slug}`)}
                    className="mt-auto flex items-center justify-between mx-5 py-3 border-t border-gray-100"
                  >
                    <span className="font-semibold text-sm transition-colors" style={{ color: colors.ctaText }}>
                      {items.length > MAXIMO_ACCESOS
                        ? `Ver las ${items.length} ${items[0].esCategoria ? 'subcategorías' : 'publicaciones'}`
                        : 'Explorar publicaciones'}
                    </span>
                    <span
                      className="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300 group-hover:translate-x-0.5"
                      style={{ backgroundColor: `${colors.primary}10`, color: colors.ctaText }}
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
